import AuthService, { type BindMessenger, type MessengerBindResult } from "~/services/AuthService";
import { ApiKey } from "~/common/API/baseApi";
import { API_ERROR_CODE, getApiErrorCode, getApiErrorMessage } from "~/common/API/apiErrors";
import { useUserStore } from "~/store/UserModule";
import { usePublisherStore } from "~/store/PublisherModule";

/**
 * Привязка мессенджера к текущему пользователю: получаем authKey, собираем
 * из него deeplink в бота и опрашиваем check-auth до результата.
 *
 * Сама привязка завершается в колбэке бота, а не в ответе на bind-start,
 * поэтому и успех, и ошибка приезжают через поллинг.
 */

const POLL_INTERVAL = 3000;
const POLL_TIMEOUT = 60000;
const GENERIC_ERROR = "Не удалось привязать аккаунт. Попробуйте снова.";

export interface MessengerOption {
  value: BindMessenger;
  label: string;
}

export const useMessengerBind = (options?: {
  side?: "advertiser" | "publisher";
  onBound?: (result: MessengerBindResult) => void;
}) => {
  const config = useRuntimeConfig();
  const userStore = useUserStore();

  // У кабинетов разные боты: ключ, выданный паблишеру, чужой бот не примет
  const isPublisher = options?.side === "publisher";
  // Токен и id паблишера живут в своём сторе, а не в рекламодательском
  const publisherStore = isPublisher ? usePublisherStore() : null;
  const telegramPrefix = String(
    (isPublisher
      ? config.public.RESOLVE_TELEGRAM_AUTHORIZE_PREFIX_PUBLISHER
      : config.public.RESOLVE_TELEGRAM_AUTHORIZE_PREFIX) || ""
  );
  const maxPrefix = String(
    (isPublisher
      ? config.public.RESOLVE_MAX_AUTHORIZE_PREFIX_PUBLISHER
      : config.public.RESOLVE_MAX_AUTHORIZE_PREFIX) || ""
  );

  // MAX появляется в интерфейсе только когда задан адрес его бота: без
  // префикса ссылка никуда не ведёт, и кнопку показывать незачем
  const available: MessengerOption[] = [
    { value: "telegram", label: "Telegram" },
    ...(maxPrefix ? [{ value: "max" as const, label: "MAX" }] : []),
  ];

  const messenger = ref<BindMessenger>("telegram");
  const authLink = ref("");
  const isLoading = ref(false);
  const errorText = ref("");
  // Ручное объединение учёток делает поддержка — на этой ошибке даём ссылку
  const needsSupport = ref(false);

  let pollingInterval: ReturnType<typeof setInterval> | null = null;
  let pollingTimeout: ReturnType<typeof setTimeout> | null = null;

  const stop = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      pollingInterval = null;
    }
    if (pollingTimeout) {
      clearTimeout(pollingTimeout);
      pollingTimeout = null;
    }
  };

  const failWith = (message: string, withSupport = false) => {
    stop();
    isLoading.value = false;
    authLink.value = "";
    errorText.value = message;
    needsSupport.value = withSupport;
  };

  /**
   * После объединения выживает одна учётка, и её id может отличаться от того,
   * под которым пользователь сидел до привязки. Токен сохраняем всегда, а при
   * смене пользователя перезагружаем страницу: на экране данные прежней учётки.
   */
  const applyResult = (result: MessengerBindResult) => {
    const previousId = String((publisherStore ? publisherStore.id : userStore.id) || "");
    const nextId = String(result.id ?? "");

    if (result.accessToken) {
      if (publisherStore) {
        publisherStore.setAuthorized(publisherStore.authKey, result.accessToken, nextId);
      } else {
        ApiKey.set(result.accessToken);
        userStore.setAuthorized(userStore.authKey, result.accessToken, nextId);
      }
    }

    stop();
    isLoading.value = false;
    options?.onBound?.(result);

    if (previousId && nextId && previousId !== nextId && import.meta.client) {
      window.location.reload();
    }
  };

  const handlePollError = (source: any) => {
    const code = getApiErrorCode(source);
    // Известный код означает, что привязка провалилась и ключ уже сгорел —
    // ждать нечего. Всё остальное считаем сетевой рябью и продолжаем опрос.
    if (code === null) return false;
    failWith(
      getApiErrorMessage(source, GENERIC_ERROR),
      code === API_ERROR_CODE.MESSENGER_MERGE_NOT_ALLOWED
    );
    return true;
  };

  const start = async (value: BindMessenger = messenger.value) => {
    stop();
    messenger.value = value;
    isLoading.value = true;
    errorText.value = "";
    needsSupport.value = false;
    authLink.value = "";

    try {
      const { authKey } = await AuthService.messengerBindStart(value);
      authLink.value = `${value === "max" ? maxPrefix : telegramPrefix}${authKey}`;

      pollingInterval = setInterval(async () => {
        try {
          const result = await AuthService.messengerBindCheck(value, authKey);
          // Ошибку бэк может отдать телом с обычным статусом
          if (handlePollError(result)) return;
          if (result?.accessToken || result?.id) applyResult(result);
        } catch (error: any) {
          handlePollError(error);
        }
      }, POLL_INTERVAL);

      pollingTimeout = setTimeout(() => {
        failWith("Время ожидания истекло. Попробуйте снова.");
      }, POLL_TIMEOUT);
    } catch (error: any) {
      failWith(getApiErrorMessage(error, "Не удалось начать привязку. Попробуйте позже."));
    }
  };

  onBeforeUnmount(stop);

  return { available, messenger, authLink, isLoading, errorText, needsSupport, start, stop };
};
